import React, { useState, useEffect } from 'react';
import { NativeSyntheticEvent, TextInputKeyPressEventData } from 'react-native';
import { translate } from '../../../services/translate';
import Toast from 'react-native-toast-message';
import DeleteModal from '../../../components/DeleteModal';
import { Pagination } from '../../../components/Pagination';
import { ClientError } from '../../../services/central-api/base-service';
import { RouteProp, useIsFocused, useRoute } from '@react-navigation/native';
import { AccessParamList } from '../../../typings/Params';
import Filter from './Filters';
import useFilters from '../../../components/Filter/hooks/useFilters';
import FilterInput from '../../../components/Filter/components/FilterInput';
import MyDataTable from '../../../components/MyDataTable';
import CustomButton from '../../../components/CustomButton';
import { useAuth } from '../../../context/Auth';
import DropdownButton from '../../../components/DropdownButton';
import { accessService } from '../../../services/central-api/access';
import WorkSchedulesModal from './WorkSchedulesModal';
import moment from 'moment';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import Card from '../../../components/Card';
import DataTableHeader from '../../../components/DataTableHeader';
import DataTableContainer from '../../../components/DataTableContainer';
import { useQuery } from '@tanstack/react-query';

export default function WorkSchedules({ navigation }: { navigation: Navigation; }) {
    const route = useRoute<RouteProp<AccessParamList, 'WorkSchedules'>>();
    const { user: authenticatedUser } = useAuth();

    const [filters, setFilters] = useFilters<AccessParamList['WorkSchedules']>({
        ...route.params,
        page: Math.max(route.params.page - 1, 0),
        limit: route.params.limit ?? 25,
    });
    const [textFilter, setTextFilter] = useState(filters.textFilter ?? '');
    const [totalItems, setTotalItems] = useState(0);

    const [isModalVisible, setModalVisible] = useState<boolean>(false);
    const [selectedWorkSchedule, setSelectedWorkSchedule] = useState<WorkSchedule>();
    const [isDeleteModalVisible, setDeleteModalVisible] = useState(false);
    const [workSchedules, setWorkSchedules] = useState<WorkSchedule[]>([]);

    const isFocused = useIsFocused();
    const queryResult = useQuery({
        queryKey: ['accessWorkSchedules', filters],
        queryFn: () => accessService.getWorkSchedules(filters),
        enabled: !!authenticatedUser && isFocused,
    });

    const isEnabled = !!authenticatedUser;
    const isLoading = isEnabled && queryResult.isFetching;

    const { data, refetch } = queryResult;

    useEffect(() => {
        if (!data) {
            return;
        }
        setWorkSchedules(data.rows);
        setTotalItems(data.count);
    }, [data]);

    async function confirmDelete(workSchedule: WorkSchedule) {
        try {
            if (workSchedule.id) {
                await accessService.deleteWorkSchedule(workSchedule.id);
                Toast.show({
                    type: 'sentinelxSuccess',
                    text1: translate('ActionSuccessfully'),
                });
                setDeleteModalVisible(false);
                refetch();

            }
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function handleFormClose() {
        setSelectedWorkSchedule(undefined);
        setModalVisible(false);
        refetch();
    }

    function handleKeyDown(e: NativeSyntheticEvent<TextInputKeyPressEventData>) {
        if (e.nativeEvent.key == 'Enter') {
            setFilters(old => ({
                ...old,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 0,
            }));

            navigation.setParams({
                ...filters,
                textFilter: textFilter !== '' ? textFilter : undefined,
                page: 1,
            });
        }
    }

    return (
        <Container>
            <Header
                title={translate('workSchedules')}
                actions={<CustomButton text={translate('register')} onPress={() => {
                    setSelectedWorkSchedule(undefined);
                    setModalVisible(true);
                }} />}
            />
            <Card>
                <DataTableHeader
                    title={translate('registeredWorkSchedules')}
                    zIndex={2}
                    filterInput={<FilterInput
                        value={textFilter}
                        placeholder={translate('typeToSearch')}
                        onChange={setTextFilter}
                        onKeyPress={handleKeyDown}
                    />}
                    filter={<Filter
                        authenticatedUser={authenticatedUser}
                        filters={filters}
                        setFilters={setFilters}
                        textFilter={textFilter}
                        setTextFilter={setTextFilter}
                    />}

                />
                <DataTableContainer
                    dataTable={<MyDataTable
                        columns={[
                            {
                                name: 'ID',
                                selector: row => row.id,
                                grow: 0
                            },
                            {
                                name: translate('name'),
                                selector: row => `${row.name} - ${row.workScheduleType === '5/2' ? translate('daily') : row.workScheduleType}`,
                                wrap: true,
                                grow: 2
                            },
                            {
                                name: translate('startReferenceDate'),
                                selector: row => moment(row.startReferenceDate).format('DD/MM/YYYY HH:mm'),
                                wrap: true,
                            },
                            {
                                name: translate('endReferenceDate'),
                                selector: row => moment(row.endReferenceDate).format('DD/MM/YYYY HH:mm'),
                                wrap: true,
                            },
                            {
                                name: translate('actingBody'),
                                selector: row => row.actingBody.name,
                                wrap: true,
                            },
                            {
                                cell: row => (
                                    <DropdownButton
                                        actions={[
                                            {
                                                content: translate('editInformation'),
                                                onPress: () => {
                                                    setSelectedWorkSchedule(row);
                                                    setModalVisible(true);
                                                }
                                            },
                                            {
                                                content: translate('delete'),
                                                variant: 'danger',
                                                onPress: () => {
                                                    setSelectedWorkSchedule(row);
                                                    setDeleteModalVisible(true);
                                                },
                                            }
                                        ]}
                                    />
                                ),
                                wrap: true,
                                button: true
                            }
                        ]}
                        data={workSchedules}
                        progressPending={isLoading}
                    />}
                    pagination={<Pagination
                        currentPage={filters.page}
                        totalItems={totalItems}
                        setPage={page => {
                            setFilters(old => ({
                                ...old,
                                page
                            }));

                            navigation.setParams({
                                ...route.params,
                                page: page + 1
                            });
                        }}
                        pageSize={filters.limit}
                        setPageSize={pageSize => {
                            setFilters(old => ({
                                ...old,
                                limit: pageSize,
                                page: 0,
                            }));

                            navigation.setParams({
                                ...route.params,
                                limit: pageSize,
                                page: 1,
                            });
                        }}
                    />}
                />
            </Card>
            <WorkSchedulesModal
                onClose={handleFormClose}
                isModalVisible={isModalVisible}
                workScheduleId={selectedWorkSchedule?.id}
            />

            {selectedWorkSchedule && <DeleteModal
                setModalVisible={setDeleteModalVisible}
                isModalVisible={isDeleteModalVisible}
                itemName={selectedWorkSchedule.name}
                onSubmit={async () => {
                    if (selectedWorkSchedule) {
                        await confirmDelete(selectedWorkSchedule);
                    }
                }}
            />}

        </Container>
    );
}
